import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface Column<T> {
  header: string | React.ReactNode;
  accessorKey?: keyof T;
  id?: string;
  cell?: (row: T) => React.ReactNode;
  width?: string;
  headerClassName?: string;
  cellClassName?: string;
}

interface DataTableProps<T> {
  title: string;
  data: T[];
  columns: Array<Column<T>>;
  customTableBody?: React.ReactNode;
  rowClassName?: string;
  cellClassName?: string;
  headerCellClassName?: string;
}

export function DataTable<T>({
  title,
  data,
  columns,
  customTableBody,
  rowClassName = "hover:bg-gray-50 border-b border-gray-100",
  cellClassName = "py-3 text-gray-700",
  headerCellClassName = "text-gray-600 font-bold py-4",
}: DataTableProps<T>) {
  return (
    <Card className="w-full border-0 shadow-sm card-modern">
      <CardHeader className="bg-white rounded-t-lg border-b border-gray-100 py-3 px-4">
        <CardTitle className="text-lg font-medium text-gray-800">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="relative w-full overflow-auto">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow className="border-b border-gray-200">
                {columns.map((column, index) => (
                  <TableHead
                    key={index}
                    className={`${headerCellClassName} ${
                      column.headerClassName || ""
                    }`}
                    style={column.width ? { width: column.width } : {}}
                  >
                    {column.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {customTableBody ? (
                customTableBody
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-gray-500"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, rowIndex) => (
                  <TableRow key={rowIndex} className={rowClassName}>
                    {columns.map((column, colIndex) => (
                      <TableCell
                        key={colIndex}
                        className={`${cellClassName} ${
                          column.cellClassName || ""
                        }`}
                        style={column.width ? { width: column.width } : {}}
                      >
                        {column.cell
                          ? column.cell(row)
                          : column.accessorKey
                          ? String(row[column.accessorKey] ?? "")
                          : ""}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
